import React, { useEffect, useState } from 'react'
import { Box, GridContainer, Row, Column, Typography, Button, P, H2, SkeletonText, SkeletonTextLine, Spinner } from '@vp/swan'
import { TokenReplacer, LinkFactory } from '@vp/digital-token-replacer-lib-ubik'

import { DOMAINS_MERCHANDISING_SECTION_LABEL, FIND_MY_DOMAIN_CTA_DETAIL, GET_STARTED_CTA_DETAIL } from '../WixDomainsPDPAnalytics'

import { getWixPrivacyPolicyUrl, getWixTermsOfUseUrl } from '@vp/digital-url-service-lib-ubik'
import { useLocale } from '@vp/digital-locale-lib-ubik'
import { getCloudinaryImage } from '../../../common/helpers/cloudinaryImageResizerHelper'
import styles from '../wixDomainsPdp.scss'
import { Hero } from '../../../../types/Hero'
import { getPartnerAccountProducts } from '@vp/digital-paid-lib-ubik'
import { useIdentity, useLogger } from '@vp/ubik-context'
import { WIX_DOMAINS_DYNAMIC_UI_ROLLOUT_LOCALES } from '../../../common/constants/experimentLocale'
interface DoubleBannerSectionProps {
  handleGetStartedNavigation: () => void
  handleWixFindMyDomainNavigation: (navigateWithouData: boolean) => void
  createWebsiteSubHero: Hero
  customersChooseWixSubHero: Hero
  isGetStartedLoading: boolean
}
const DoubleBannerSection = ({
  createWebsiteSubHero,
  isGetStartedLoading,
  customersChooseWixSubHero,
  handleGetStartedNavigation,
  handleWixFindMyDomainNavigation,
}: DoubleBannerSectionProps) => {
  const { languageCode, culture: locale } = useLocale()
  const [displaySkeleton, setDisplaySkeleton] = useState(true)
  const { auth, isIdentityInitialized } = useIdentity()
  const canonicalId = auth?.getCanonicalId()
  const isSignedIn = auth?.isSignedIn()
  const logger = useLogger()
  const [isPremium, setIsPremium] = useState(false)
  const [numberOfSites, setNumberOfSites] = useState<number | null | undefined>(null)

  useEffect(() => {
    async function fetchData(customerId: string) {
      try {
        const data = await getPartnerAccountProducts(customerId)
        setNumberOfSites(data?.wixSites?.length)
        setIsPremium(data.wixSites?.some((site: any) => site.premium) || false)
      } catch (error) {
        setNumberOfSites(0)
        logger.warn('Error while loading getPartnerAccountProducts' + error)
      } finally {
        setDisplaySkeleton(false)
      }
    }
    if (!isIdentityInitialized || !canonicalId) {
      return
    }
    if (!isSignedIn) {
      setNumberOfSites(0)
      setDisplaySkeleton(false)
      return
    }
    fetchData(canonicalId)
  }, [canonicalId, logger, isSignedIn])

  const createYourOwnCustomSiteData = (isGetStartedLoading: boolean) => {
    let title: string,
      description: string,
      ctaName: string,
      footer: string = ''
    const backgroundImage = getCloudinaryImage(createWebsiteSubHero.mediaLink?.[0].secure_url ?? '')
    if (numberOfSites === 0 || WIX_DOMAINS_DYNAMIC_UI_ROLLOUT_LOCALES.indexOf(locale) !== 0) {
      title = createWebsiteSubHero?.title ?? ''
      description = createWebsiteSubHero?.description ?? ''
      ctaName = createWebsiteSubHero.actions?.[0].name ?? ''
      footer = createWebsiteSubHero.heroMetadata?.[0].description ?? ''
    } else {
      if (isPremium) {
        // Same data for now as in the else block,
        // but TBD — we may need to show different data (e.g., a "Workspace" banner) later, depending on PM decision
        title = createWebsiteSubHero.heroMetadata?.[1].features?.[1].name ?? ''
        description = createWebsiteSubHero.heroMetadata?.[1].features?.[1].description ?? ''
        ctaName = createWebsiteSubHero.heroMetadata?.[1].features?.[1].altText ?? ''
      } else {
        title = createWebsiteSubHero.heroMetadata?.[1].features?.[1].name ?? ''
        description = createWebsiteSubHero.heroMetadata?.[1].features?.[1].description ?? ''
        ctaName = createWebsiteSubHero.heroMetadata?.[1].features?.[1].altText ?? ''
      }
    }

    return (
      <Column
        span={12}
        spanMd={6}
        spanLg={6}
        spanXl={6}
        className={styles.wixDomainsPdpCustomersContainer}
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundColor: '#f3e8c1',
        }}
      >
        <Box>
          <H2 fontSize="x2large" mb={0}>
            {title}
          </H2>

          <Typography m={0} mt="6" component="p" fontSize="small">
            {description}
          </Typography>

          <Box mt="6">{displayGetStartedButtons(ctaName, footer, isGetStartedLoading)[0]}</Box>
        </Box>
      </Column>
    )
  }

  const displayGetStartedButtons = (ctaName: string, footer: string, isGetStartedLoading: boolean) => {
    const footerJSX = (
      <P fontSize="xsmall" mt="6" mb={0} className={styles.wixDomainsPdpCustomersFooterTextWrapper}>
        <TokenReplacer
          template={footer}
          replacements={{
            link: LinkFactory({
              wixTermsOfUseUrl: getWixTermsOfUseUrl(languageCode),
              wixPrivacyPolicyUrl: getWixPrivacyPolicyUrl(languageCode),
            }),
          }}
          convertMarkdown
        />
      </P>
    )

    return [
      // First sub hero CTA
      <React.Fragment key="first-cta">
        <Button
          skin="secondary"
          onClick={() => handleGetStartedNavigation()}
          data-section={DOMAINS_MERCHANDISING_SECTION_LABEL}
          data-position="2"
          data-translation={GET_STARTED_CTA_DETAIL}
          className={styles.wixDomainsPdpCustomersContainerAccessibleBtn}
        >
          {ctaName}
          {isGetStartedLoading && <Spinner accessibleText="Loading..." />}
        </Button>
        {footerJSX}
      </React.Fragment>,

      // Second sub hero CTA
      <React.Fragment key="second-cta">
        <Button
          skin="secondary"
          onClick={() => handleWixFindMyDomainNavigation(true)}
          data-section={DOMAINS_MERCHANDISING_SECTION_LABEL}
          data-position="3"
          data-translation={FIND_MY_DOMAIN_CTA_DETAIL}
          className={styles.wixDomainsPdpCustomersContainerAccessibleBtn}
        >
          {ctaName}
        </Button>
      </React.Fragment>,
    ]
  }

  return (
    <Box as="article" className="wix-domains-pdp__sub-hero__container" mt={3}>
      <GridContainer>
        <Row>
          <>
            {displaySkeleton && (
              <Column
                span={12}
                spanMd={6}
                spanLg={6}
                spanXl={6}
                className="wix-domains-pdp__customers__container"
                style={{
                  backgroundImage: `url(${getCloudinaryImage(createWebsiteSubHero.mediaLink?.[0].secure_url ?? '')})`,
                  backgroundColor: '#f3e8c1',
                }}
              >
                <Box>
                  <SkeletonText>
                    <SkeletonTextLine fontSize="x2large" />
                    <SkeletonTextLine fontSize="x2large" />
                  </SkeletonText>
                  <SkeletonText mt="6">
                    <SkeletonTextLine fontSize="standard" />
                    <SkeletonTextLine fontSize="standard" width="narrow" />
                    <SkeletonTextLine fontSize="standard" />
                  </SkeletonText>
                  <Spinner mt={6} accessibleText="Loading" />
                  <SkeletonText mt="6">
                    <SkeletonTextLine fontSize="small" />
                    <SkeletonTextLine fontSize="small" width="narrow" />
                    <SkeletonTextLine fontSize="small" />
                    <SkeletonTextLine fontSize="small" />
                    <SkeletonTextLine fontSize="small" />
                  </SkeletonText>
                </Box>
              </Column>
            )}
            {!displaySkeleton && createYourOwnCustomSiteData(isGetStartedLoading)}
            {[customersChooseWixSubHero].map((subHero, index) => (
              <Column
                key={index}
                span={12}
                spanMd={6}
                spanLg={6}
                spanXl={6}
                className="wix-domains-pdp__customers__container"
                style={{
                  backgroundImage: `url(${getCloudinaryImage(subHero.mediaLink?.[0].secure_url ?? '')})`,
                }}
              >
                <Box>
                  <H2 fontSize="x2large" mb={0}>
                    {subHero.title}
                  </H2>

                  <Typography m={0} mt="6" component="p" fontSize="small">
                    {subHero.description}
                  </Typography>

                  <Box mt="6">
                    {displayGetStartedButtons(subHero.actions?.[0].name ?? '', subHero.heroMetadata?.[0].description ?? '', isGetStartedLoading)[index + 1]}
                  </Box>
                </Box>
              </Column>
            ))}
          </>
        </Row>
      </GridContainer>
    </Box>
  )
}

export default DoubleBannerSection
